import { useEffect, useState } from 'react';
import {
  Bot,
  Workflow,
  Coins,
  Inbox,
  Plus,
  ArrowRight,
  MessageSquare,
  Play,
  Wallet as WalletIcon,
  Sparkles,
  type LucideIcon,
} from 'lucide-react';
import { useAgentStudioNavigate } from '../lib/routes';
import { Card } from '../components/ui/Card';
import { Button } from '../components/ui/Button';
import { Skeleton, EmptyState } from '../components/ui/States';
import { Modal } from '../components/ui/Modal';
import { Field, Input, Textarea } from '../components/ui/Field';
import { RunStatusBadge } from '../components/StatusBadges';
import { useAuth } from '../auth/AuthContext';
import { agentsService } from '../services/agentsService';
import { flowsService } from '../services/flowsService';
import { runsService } from '../services/runsService';
import { approvalsService } from '../services/approvalsService';
import { usageService, type Wallet } from '../services/usageService';
import { budgetService } from '../services/budgetService';
import { ApiError } from '../services/apiClient';
import { newFlow } from '../lib/factories';
import { formatRelativeTime, formatUSD, cn } from '../lib/utils';
import { toast } from '../store/uiStore';
import type { Agent, Flow, Run } from '../types';
import { AGENT_STUDIO_PERMISSIONS as P, useAgentStudioPermissions } from '../lib/permissions';

export function DashboardPage() {
  const navigate = useAgentStudioNavigate();
  const { user } = useAuth();
  const { has } = useAgentStudioPermissions();
  const canManageAgents = has(P.agentsManage);
  const canChat = has(P.agentsChat);
  const canManageFlows = has(P.flowsManage);
  const canRequestBudget = has(P.budgetRequest);
  const canViewActivities = has(P.activitiesView);
  const [agents, setAgents] = useState<Agent[]>([]);
  const [flows, setFlows] = useState<Flow[]>([]);
  const [runs, setRuns] = useState<Run[]>([]);
  const [wallet, setWallet] = useState<Wallet | null>(null);
  const [pending, setPending] = useState(0);
  const [loading, setLoading] = useState(true);

  // Pedir mais saldo
  const [reqOpen, setReqOpen] = useState(false);
  const [reqAmount, setReqAmount] = useState('5');
  const [reqReason, setReqReason] = useState('');
  const [requesting, setRequesting] = useState(false);

  useEffect(() => {
    Promise.all([
      agentsService.list(),
      flowsService.list(),
      runsService.list().catch(() => []),
      usageService.wallet().catch(() => null),
      approvalsService.pendingCountForUser().catch(() => 0),
    ]).then(([a, f, r, w, p]) => {
      setAgents(a);
      setFlows(f);
      setRuns(r);
      setWallet(w);
      setPending(p);
      setLoading(false);
    });
  }, []);

  const firstName = (user?.name ?? 'por aqui').split(' ')[0];
  const activeAgents = agents.filter((a) => (a.status ?? 'draft') !== 'archived');
  const usableFlows = [...flows].sort((a, b) => Number(!!b.favorite) - Number(!!a.favorite));
  const recentRuns = [...runs].sort((a, b) => (b.startedAt ?? '').localeCompare(a.startedAt ?? '')).slice(0, 5);
  const lowBalance = !!wallet && wallet.available <= Math.max(0.5, wallet.monthlyBudget * 0.1);

  async function createFlow() {
    const flow = await flowsService.save(newFlow());
    navigate(`/flows/${flow.id}`);
  }

  async function submitRequest() {
    const amount = Number(reqAmount);
    if (!(amount > 0)) { toast.error('Informe um valor maior que zero.'); return; }
    setRequesting(true);
    try {
      await budgetService.requestGrant(amount, reqReason);
      toast.success('Pedido enviado ao admin da área.');
      setReqOpen(false);
      setReqReason('');
    } catch (err) {
      toast.error(err instanceof ApiError ? err.message : 'Falha ao solicitar.');
    } finally {
      setRequesting(false);
    }
  }

  return (
    <>
      <div className="border-b border-line bg-white px-8 py-5">
        <h1 className="flex items-center gap-2 text-xl font-semibold text-ink">
          Olá, {firstName} <span className="text-2xl">👋</span>
        </h1>
        <p className="mt-0.5 text-sm text-slate">O que você quer fazer hoje?</p>
      </div>

      <div className="flex-1 overflow-y-auto scrollbar-thin p-8">
        {/* Linha 1: saldo + pendências + ações rápidas */}
        <div className="grid grid-cols-1 gap-5 lg:grid-cols-3">
          {/* Saldo (hero) */}
          <Card className="lg:col-span-2 p-6">
            <div className="flex items-start justify-between">
              <div className="flex items-center gap-2 text-sm font-medium text-slate">
                <WalletIcon className="h-4 w-4" /> Seu saldo disponível
              </div>
              {wallet && canRequestBudget && (
                <Button variant={lowBalance ? 'primary' : 'secondary'} size="sm" icon={Coins} onClick={() => setReqOpen(true)}>
                  Solicitar mais saldo
                </Button>
              )}
            </div>

            {loading ? (
              <Skeleton className="mt-4 h-10 w-48" />
            ) : wallet ? (
              <>
                <p className={cn('mt-2 text-4xl font-semibold', wallet.available <= 0 ? 'text-brand-orange' : 'text-ink')}>
                  {formatUSD(wallet.available)}
                </p>
                <div className="mt-4 h-2 w-full overflow-hidden rounded-full bg-cloud">
                  <div
                    className={cn('h-full rounded-full', wallet.available <= 0 ? 'bg-brand-orange' : lowBalance ? 'bg-warning' : 'bg-success')}
                    style={{ width: `${wallet.monthlyBudget > 0 ? Math.min(100, Math.round((wallet.monthlyRemaining / wallet.monthlyBudget) * 100)) : (wallet.available > 0 ? 100 : 0)}%` }}
                  />
                </div>
                <div className="mt-3 flex flex-wrap gap-x-6 gap-y-1 text-xs text-slate">
                  <span>Budget do mês: <strong className="text-ink">{formatUSD(wallet.monthlyBudget)}</strong></span>
                  <span>Gasto: <strong className="text-ink">{formatUSD(wallet.monthlySpent)}</strong></span>
                  {wallet.topup > 0 && <span>Saldo extra: <strong className="text-ink">{formatUSD(wallet.topup)}</strong></span>}
                  {!!wallet.grants && wallet.grants > 0 && <span>Liberado pela área: <strong className="text-ink">{formatUSD(wallet.grants)}</strong></span>}
                </div>
                {lowBalance && (
                  <p className="mt-3 rounded-lg bg-brand-orange/5 px-3 py-2 text-xs text-brand-orange">
                    {wallet.available <= 0 ? 'Seu saldo acabou.' : 'Seu saldo está baixo.'} Solicite mais saldo para continuar usando os agentes e fluxos.
                  </p>
                )}
              </>
            ) : (
              <p className="mt-4 text-sm text-slate">Saldo não disponível.</p>
            )}
          </Card>

          {/* Pendências + atalhos */}
          <div className="space-y-4">
            {canViewActivities && (
              <button
              onClick={() => navigate('/activities')}
              className="flex w-full items-center gap-3 rounded-xl border border-line bg-white p-4 text-left transition-all hover:shadow-soft"
            >
              <div className={cn('flex h-11 w-11 items-center justify-center rounded-xl', pending > 0 ? 'bg-brand-orange text-white' : 'bg-cloud text-slate')}>
                <Inbox className="h-5 w-5" />
              </div>
              <div className="flex-1">
                <p className="text-sm font-semibold text-ink">Minhas Atividades</p>
                <p className="text-xs text-slate">{pending > 0 ? `${pending} pendência(s) aguardando você` : 'Nada pendente'}</p>
              </div>
              <ArrowRight className="h-4 w-4 text-slate" />
              </button>
            )}

            {canManageAgents && <ShortcutCard icon={Bot} title="Criar agente" desc="Persona, arquivos e guardrails" onClick={() => navigate('/agents')} />}
            {canManageFlows && <ShortcutCard icon={Plus} title="Criar fluxo" desc="Encadeie agentes no canvas" onClick={createFlow} accent />}
          </div>
        </div>

        {/* Converse com um agente */}
        <Section
          title="Converse com um agente"
          icon={MessageSquare}
          actionLabel="Ver todos"
          onAction={() => navigate('/agents')}
        >
          {loading ? (
            <CardGridSkeleton />
          ) : activeAgents.length === 0 ? (
            <EmptyState icon={Bot} title="Nenhum agente ainda" description="Crie seu primeiro agente para começar a conversar." action={<Button variant="primary" icon={Plus} onClick={() => navigate('/agents')}>Criar agente</Button>} />
          ) : (
            <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 xl:grid-cols-3">
              {activeAgents.slice(0, 6).map((agent, index) => (
                <Card key={agent.id || `agent-${index}`} className="flex flex-col p-4 transition-all hover:shadow-soft">
                  <div className="flex items-start gap-3">
                    <div className="flex h-10 w-10 shrink-0 items-center justify-center rounded-xl bg-brand-orange text-white">
                      <Bot className="h-5 w-5" />
                    </div>
                    <div className="min-w-0 flex-1">
                      <p className="truncate text-sm font-semibold text-ink">{agent.name}</p>
                      <p className="line-clamp-2 text-xs text-slate">{agent.description || 'Sem descrição.'}</p>
                    </div>
                  </div>
                  {canChat && (
                    <Button variant="primary" size="sm" icon={MessageSquare} className="mt-3 w-full" onClick={() => navigate(`/agents/${agent.id}/chat`)}>
                      Conversar
                    </Button>
                  )}
                </Card>
              ))}
            </div>
          )}
        </Section>

        {/* Fluxos + atividade recente */}
        <div className="mt-8 grid grid-cols-1 gap-6 lg:grid-cols-2">
          {/* Seus fluxos */}
          <Card>
            <SectionHeader title="Seus fluxos" icon={Workflow} actionLabel="Ver todos" onAction={() => navigate('/flows')} />
            <div className="divide-y divide-line">
              {loading ? (
                Array.from({ length: 3 }).map((_, i) => <RowSkeleton key={i} />)
              ) : usableFlows.length === 0 ? (
                <div className="px-5 py-8 text-center text-sm text-slate">Nenhum fluxo ainda.</div>
              ) : (
                usableFlows.slice(0, 5).map((flow, index) => (
                  <button
                    key={flow.id || `flow-${index}`}
                    onClick={() => navigate(`/flows/${flow.id}`)}
                    className="flex w-full items-center gap-4 px-5 py-3.5 text-left transition-colors hover:bg-cloud"
                  >
                    <div className="flex h-9 w-9 shrink-0 items-center justify-center rounded-lg bg-navy text-white">
                      <Workflow className="h-4 w-4" />
                    </div>
                    <div className="min-w-0 flex-1">
                      <p className="truncate text-sm font-medium text-ink">{flow.name}</p>
                      <p className="truncate text-xs text-slate">{flow.description || 'Sem descrição.'}</p>
                    </div>
                    <Play className="h-4 w-4 shrink-0 text-brand-orange" />
                  </button>
                ))
              )}
            </div>
          </Card>

          {/* Atividade recente */}
          <Card>
            <SectionHeader title="Atividade recente" icon={Sparkles} actionLabel="Ver execuções" onAction={() => navigate('/runs')} />
            <div className="divide-y divide-line">
              {loading ? (
                Array.from({ length: 3 }).map((_, i) => <RowSkeleton key={i} />)
              ) : recentRuns.length === 0 ? (
                <div className="px-5 py-8 text-center text-sm text-slate">Nenhuma execução ainda. Rode um fluxo para começar.</div>
              ) : (
                recentRuns.map((run, index) => (
                  <button
                    key={run.id || `run-${index}`}
                    onClick={() => navigate(`/runs/${run.id}`)}
                    className="flex w-full items-center gap-4 px-5 py-3.5 text-left transition-colors hover:bg-cloud"
                  >
                    <div className="flex h-9 w-9 shrink-0 items-center justify-center rounded-full bg-cloud">
                      <Workflow className="h-4 w-4 text-slate" />
                    </div>
                    <div className="min-w-0 flex-1">
                      <p className="truncate text-sm font-medium text-ink">{run.flowName}</p>
                      <p className="text-xs text-slate">
                        {formatRelativeTime(run.startedAt)}
                        {run.costBRL != null ? ` · ${formatUSD(run.costBRL)}` : ''}
                      </p>
                    </div>
                    <RunStatusBadge status={run.status} />
                  </button>
                ))
              )}
            </div>
          </Card>
        </div>
      </div>

      {/* Modal: solicitar mais saldo */}
      <Modal
        open={reqOpen}
        onClose={() => setReqOpen(false)}
        title="Solicitar mais saldo"
        footer={
          <>
            <Button variant="ghost" onClick={() => setReqOpen(false)}>Cancelar</Button>
            <Button variant="primary" icon={Coins} loading={requesting} onClick={submitRequest}>Enviar pedido</Button>
          </>
        }
      >
        <p className="mb-3 text-sm text-slate">
          O valor será debitado do budget da sua área após aprovação do administrador.
        </p>
        <Field label="Valor (US$)">
          <Input type="number" min={1} step="1" value={reqAmount} onChange={(e) => setReqAmount(e.target.value)} />
        </Field>
        <Field label="Justificativa" className="mb-0">
          <Textarea rows={3} value={reqReason} onChange={(e) => setReqReason(e.target.value)} placeholder="Para que você precisa do saldo extra?" />
        </Field>
      </Modal>
    </>
  );
}

function Section({ title, icon: Icon, actionLabel, onAction, children }: { title: string; icon: LucideIcon; actionLabel: string; onAction: () => void; children: React.ReactNode }) {
  return (
    <div className="mt-8">
      <div className="mb-3 flex items-center justify-between">
        <h2 className="flex items-center gap-2 text-base font-semibold text-ink">
          <Icon className="h-4 w-4 text-brand-orange" /> {title}
        </h2>
        <Button variant="ghost" size="sm" iconRight={ArrowRight} onClick={onAction}>{actionLabel}</Button>
      </div>
      {children}
    </div>
  );
}

function SectionHeader({ title, icon: Icon, actionLabel, onAction }: { title: string; icon: LucideIcon; actionLabel: string; onAction: () => void }) {
  return (
    <div className="flex items-center justify-between border-b border-line px-5 py-4">
      <h2 className="flex items-center gap-2 text-base font-semibold text-ink">
        <Icon className="h-4 w-4 text-brand-orange" /> {title}
      </h2>
      <Button variant="ghost" size="sm" iconRight={ArrowRight} onClick={onAction}>{actionLabel}</Button>
    </div>
  );
}

function ShortcutCard({ icon: Icon, title, desc, onClick, accent }: { icon: LucideIcon; title: string; desc: string; onClick: () => void; accent?: boolean }) {
  return (
    <button
      onClick={onClick}
      className={cn('group flex w-full items-center gap-3 rounded-xl border p-4 text-left transition-all hover:shadow-soft', accent ? 'border-brand-orange/30 bg-brand-orange/5' : 'border-line bg-white')}
    >
      <div className={cn('flex h-11 w-11 items-center justify-center rounded-xl text-white', accent ? 'bg-brand-orange' : 'bg-navy')}>
        <Icon className="h-5 w-5" />
      </div>
      <div className="flex-1">
        <p className="text-sm font-semibold text-ink">{title}</p>
        <p className="text-xs text-slate">{desc}</p>
      </div>
      <ArrowRight className="h-4 w-4 text-slate transition-transform group-hover:translate-x-1" />
    </button>
  );
}

function CardGridSkeleton() {
  return (
    <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 xl:grid-cols-3">
      {Array.from({ length: 3 }).map((_, i) => <Skeleton key={i} className="h-28 w-full" />)}
    </div>
  );
}

function RowSkeleton() {
  return (
    <div className="flex items-center gap-4 px-5 py-3.5">
      <Skeleton className="h-9 w-9 rounded-full" />
      <Skeleton className="h-4 flex-1" />
      <Skeleton className="h-5 w-20" />
    </div>
  );
}
